import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  buildManuscriptArchiveUrl,
  formatArchiveSize,
} from "@/lib/contest-archive";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_CONTEST_BULK_SET_SCREENING,
  ADMIN_CONTEST_MANUSCRIPT_ARCHIVE,
  ADMIN_CONTEST_RECOUNT_CHAR_COUNT,
  ADMIN_CONTEST_SET_FIELD_NOTE,
  ADMIN_CONTEST_SET_SCREENING,
  ADMIN_CONTEST_SUBMISSION_DOWNLOAD_URL,
  ADMIN_CONTEST_SUBMISSION_PREVIEW,
} from "@/lib/graphql/mutations/contest-submissions";
import {
  ADMIN_CONTEST_SCREENING_STATS,
  ADMIN_CONTEST_SUBMISSIONS,
  ADMIN_CONTEST_SUBMISSION_DETAIL,
} from "@/lib/graphql/queries/contest-submissions";
import type { PaginationInput, PaginationMeta } from "@/types/common";
import type {
  ContestChecklistItem,
  ContestFieldNote,
  ContestManuscriptArchive,
  ContestManuscriptArchiveInput,
  ContestScreening,
  ContestScreeningResult,
  ContestScreeningStats,
  ContestSubmissionDetail,
  ContestSubmissionListItem,
  ContestSubmissionPreview,
  ContestSubmissionsFilters,
} from "@/types/contest-submission";

export const contestSubmissionKeys = {
  all: ["contestSubmissions"] as const,
  lists: () => [...contestSubmissionKeys.all, "list"] as const,
  list: (params: ContestSubmissionsFilters & PaginationInput) =>
    [...contestSubmissionKeys.lists(), params] as const,
  details: () => [...contestSubmissionKeys.all, "detail"] as const,
  detail: (submissionId: string) =>
    [...contestSubmissionKeys.details(), submissionId] as const,
  stats: () => [...contestSubmissionKeys.all, "stats"] as const,
  // detail 하위에 두지 않는다 — 심사 저장의 detail 무효화가 미리보기
  // 재발급(파싱/presign)을 유발하면 안 됨.
  preview: (submissionId: string) =>
    [...contestSubmissionKeys.all, "preview", submissionId] as const,
};

/** 심사 진행 현황 — 판정 mutation 들이 keys.all 무효화로 함께 갱신된다. */
export function contestScreeningStatsQueryOptions() {
  return queryOptions({
    queryKey: contestSubmissionKeys.stats(),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminContestScreeningStats: ContestScreeningStats;
      }>(ADMIN_CONTEST_SCREENING_STATS);
      return data.adminContestScreeningStats;
    },
    staleTime: 30_000,
  });
}

/**
 * 원고 미리보기. 서버는 감사로그·단발성 presigned URL 때문에 mutation 으로
 * 노출하지만, 클라이언트는 Suspense/캐싱이 필요해 queryFn 에서 호출한다.
 * PDF presigned URL TTL 이 10분이라 staleTime 8분 내 재마운트는 재사용,
 * 그 뒤엔 새 URL 발급(서버 감사로그에 남는 것이 의도된 동작).
 */
export function contestSubmissionPreviewQueryOptions(submissionId: string) {
  return queryOptions({
    queryKey: contestSubmissionKeys.preview(submissionId),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminContestSubmissionPreview: ContestSubmissionPreview;
      }>(ADMIN_CONTEST_SUBMISSION_PREVIEW, { submissionId });
      return data.adminContestSubmissionPreview;
    },
    staleTime: 8 * 60_000,
    gcTime: 10 * 60_000,
    // hwp/docx 최초 변환은 수 초 걸릴 수 있음 — 자동 재시도로 파싱을 중복 유발하지 않는다.
    retry: false,
  });
}

interface AdminContestSubmissionsResponse {
  items: ContestSubmissionListItem[];
  meta: PaginationMeta;
}

export function contestSubmissionsQueryOptions(
  filters: ContestSubmissionsFilters,
  pagination: PaginationInput,
) {
  return queryOptions({
    queryKey: contestSubmissionKeys.list({ ...filters, ...pagination }),
    queryFn: async () => {
      const input = {
        page: pagination.page,
        limit: pagination.limit,
        // 빈 문자열은 서버 측 부분일치 검색에서 매칭이 사라지므로 undefined 로 표준화.
        search: filters.search?.trim() || undefined,
        screeningResult: filters.screeningResult || undefined,
        userId: filters.userId || undefined,
      };
      const data = await authenticatedRequest<{
        adminContestSubmissions: AdminContestSubmissionsResponse;
      }>(ADMIN_CONTEST_SUBMISSIONS, { input });
      return data.adminContestSubmissions;
    },
    // 페이지 전환 시 깜빡임 방지.
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    // 집계중(PENDING) 건이 보이는 동안 자동 새로고침 — 나갔다 들어올 필요 없게.
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (item) => item.charCount?.status === "PENDING",
      )
        ? 5_000
        : false,
  });
}

export function contestSubmissionDetailQueryOptions(submissionId: string) {
  return queryOptions({
    queryKey: contestSubmissionKeys.detail(submissionId),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminContestSubmissionDetail: ContestSubmissionDetail;
      }>(ADMIN_CONTEST_SUBMISSION_DETAIL, { submissionId });
      return data.adminContestSubmissionDetail;
    },
    staleTime: 60_000,
    // 글자수 집계중이면 자동 새로고침.
    refetchInterval: (query) =>
      query.state.data?.charCount?.status === "PENDING" ? 5_000 : false,
  });
}

/**
 * 다운로드 presigned URL 발급. 호출 시점에 새 URL 생성 후 새 탭으로 오픈.
 * mutation 으로 두어 캐싱 안 함.
 */
export function useContestSubmissionDownloadUrl() {
  return useMutation<string, Error, string>({
    mutationFn: async (submissionId) => {
      const data = await authenticatedRequest<{
        adminContestSubmissionDownloadUrl: string;
      }>(ADMIN_CONTEST_SUBMISSION_DOWNLOAD_URL, { submissionId });
      return data.adminContestSubmissionDownloadUrl;
    },
  });
}

const SCREENING_RESULT_LABEL: Record<ContestScreeningResult, string> = {
  PENDING: "검토중",
  PASS: "적격",
  FAIL: "부적격",
};

/**
 * 원고 zip 일괄 다운로드. 서버에서 토큰을 받아 곧바로 REST 다운로드로 이동시킨다
 * (토큰 TTL 이 2분이라 발급과 이동 사이를 벌리지 않는다).
 * 파일은 브라우저가 직접 스트리밍해 받으므로 탭 이동/메모리 부담이 없다.
 */
export function useManuscriptArchiveDownload() {
  return useMutation<
    ContestManuscriptArchive,
    Error,
    ContestManuscriptArchiveInput
  >({
    mutationFn: async (input) => {
      const data = await authenticatedRequest<{
        adminContestManuscriptArchive: ContestManuscriptArchive;
      }>(ADMIN_CONTEST_MANUSCRIPT_ARCHIVE, { input });
      return data.adminContestManuscriptArchive;
    },
    onSuccess: (archive) => {
      window.location.href = buildManuscriptArchiveUrl(archive.token);
      toast.success(
        `원고 ${archive.count}건(${formatArchiveSize(archive.totalBytes)}) 다운로드를 시작합니다`,
      );
    },
    onError: (e) => {
      toast.error((e as Error)?.message ?? "원고 다운로드에 실패했습니다");
    },
  });
}

interface SetScreeningInput {
  submissionId: string;
  result: ContestScreeningResult;
  memo?: string;
  /** 전달 시 full replace, 생략 시 서버가 기존 값 보존 */
  checklist?: ContestChecklistItem[];
}

/** 1차 심사 단건 판정. 목록·상세 캐시 무효화. */
export function useSetScreening() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetScreeningInput) => {
      const data = await authenticatedRequest<{
        adminContestSetScreening: { _id: string; screening: ContestScreening };
      }>(ADMIN_CONTEST_SET_SCREENING, { input });
      return data.adminContestSetScreening;
    },
    onSuccess: (_updated, input) => {
      queryClient.invalidateQueries({
        queryKey: contestSubmissionKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: contestSubmissionKeys.detail(input.submissionId),
      });
      queryClient.invalidateQueries({
        queryKey: contestSubmissionKeys.stats(),
      });
      toast.success(
        `심사 결과를 저장했습니다 (${SCREENING_RESULT_LABEL[input.result]})`,
      );
    },
    onError: (e) => {
      toast.error((e as Error)?.message ?? "심사 결과 저장에 실패했습니다");
    },
  });
}

interface BulkSetScreeningInput {
  submissionIds: string[];
  result: ContestScreeningResult;
}

/** 1차 심사 일괄 판정 (메모 유지). */
export function useBulkSetScreening() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkSetScreeningInput) => {
      const data = await authenticatedRequest<{
        adminContestBulkSetScreening: {
          updatedCount: number;
          skippedIds: string[];
        };
      }>(ADMIN_CONTEST_BULK_SET_SCREENING, { input });
      return data.adminContestBulkSetScreening;
    },
    onSuccess: (result, input) => {
      queryClient.invalidateQueries({
        queryKey: contestSubmissionKeys.all,
      });
      const label = SCREENING_RESULT_LABEL[input.result];
      if (result.skippedIds.length > 0) {
        toast.warning(
          `${result.updatedCount}건 ${label} 처리, ${result.skippedIds.length}건은 대상이 아니어서 건너뛰었습니다`,
        );
      } else {
        toast.success(`${result.updatedCount}건을 ${label} 처리했습니다`);
      }
    },
    onError: (e) => {
      toast.error((e as Error)?.message ?? "일괄 판정에 실패했습니다");
    },
  });
}

interface SetFieldNoteInput {
  submissionId: string;
  field: string;
  /** 빈 문자열/생략 = 삭제 */
  note?: string;
}

/** 필드별 심사 메모 즉시 저장. 상세 캐시만 무효화(목록엔 미노출). */
export function useSetFieldNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetFieldNoteInput) => {
      const data = await authenticatedRequest<{
        adminContestSetFieldNote: {
          _id: string;
          reviewNotes: ContestFieldNote[] | null;
        };
      }>(ADMIN_CONTEST_SET_FIELD_NOTE, { input });
      return data.adminContestSetFieldNote;
    },
    onSuccess: (_updated, input) => {
      queryClient.invalidateQueries({
        queryKey: contestSubmissionKeys.detail(input.submissionId),
      });
      toast.success(input.note?.trim() ? "메모를 저장했습니다" : "메모를 삭제했습니다");
    },
    onError: (e) => {
      toast.error((e as Error)?.message ?? "메모 저장에 실패했습니다");
    },
  });
}

/** 글자수 재계산 요청. submissionId 없으면 누락분 전체 백필. */
export function useRecountCharCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (submissionId?: string) => {
      const data = await authenticatedRequest<{
        adminContestRecountCharCount: number;
      }>(ADMIN_CONTEST_RECOUNT_CHAR_COUNT, {
        submissionId: submissionId ?? null,
      });
      return data.adminContestRecountCharCount;
    },
    onSuccess: (enqueued) => {
      // 집계는 비동기(BullMQ) — 즉시 값이 바뀌진 않지만 PENDING 상태 반영을 위해 무효화.
      queryClient.invalidateQueries({
        queryKey: contestSubmissionKeys.all,
      });
      // enqueue 직후엔 아직 PENDING 마킹 전일 수 있어 잠시 뒤 한 번 더 —
      // 이 시점에 PENDING 이 보이면 목록/상세의 refetchInterval 폴링이 이어받는다.
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: contestSubmissionKeys.all,
        });
      }, 4_000);
      toast.success(
        enqueued > 0
          ? `글자수 재계산을 요청했습니다 (${enqueued}건) — 잠시 후 새로고침하면 반영됩니다`
          : "재계산할 대상이 없습니다",
      );
    },
    onError: (e) => {
      toast.error((e as Error)?.message ?? "재계산 요청에 실패했습니다");
    },
  });
}
